/* eslint import/prefer-default-export: "off" */
import type { Tenant, User } from '@src/redux/reducers/auth/default.state'
import { getFeatureFlagAsync } from '@src/endpoints/feature.flag'

interface MetaDataOptions {
  tenant: Tenant
  user: User
}

interface GetMetaDataOptions extends MetaDataOptions {
  useFeatureFlag: boolean
}

// Computes the SHA-256 digest of a string with Web Crypto
// Source: https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest

async function sha256(str) {
  // Get the string as arraybuffer.
  const buffer = new TextEncoder().encode(str)
  const hash = await crypto.subtle.digest('SHA-256', buffer)
  return hex(hash)
}

function hex(buffer: ArrayBuffer) {
  let digest = ''
  const view = new DataView(buffer)
  for (let i = 0; i < view.byteLength; i += 4) {
    // We use getUint32 to reduce the number of iterations (notice the `i += 4`)
    const value = view.getUint32(i)
    // toString(16) will transform the integer into the corresponding hex string
    // but will remove any initial "0"
    const stringValue = value.toString(16)
    // One Uint32 element is 4 bytes or 8 hex chars (it would also work with 4
    // chars for Uint16 and 2 chars for Uint8)
    const padding = '00000000'
    const paddedValue = (padding + stringValue).slice(-padding.length)
    digest += paddedValue
  }

  return digest
}

export async function getPendoMetaData({
  tenant,
  user,
  useFeatureFlag
}: GetMetaDataOptions): Promise<pendo.InitOptions> {
  const { tenant_id } = tenant || {}
  const { email, userSource, role } = user || {}
  if (tenant_id && email) {
    const visitorId = await sha256(`${tenant_id}_${userSource}_${email}`)

    const visitor: pendo.IdentityMetadata = {
      id: visitorId, // Required if user is logged in
      role, // Optional
      tenant_id,
      user_source: userSource
      // You can add any additional visitor level key-values here,
      // as long as it's not one of the above reserved names.
    }
    const account: pendo.IdentityMetadata = {
      id: tenant_id, // Highly recommended
      name: `Cloud Access Management - ${tenant_id}` // Optional
      // You can add any additional account level key-values here,
      // as long as it's not one of the above reserved names.
    }
    if (useFeatureFlag) {
      const { value } = await getFeatureFlagAsync(
        'CAM_PERM_20201022_ENABLE_PENDO'
      )
      const initializedPendo = pendo && pendo.isReady()
      if (value || initializedPendo) {
        return { account, visitor }
      }
      return null
    }
    return { account, visitor }
  }
}

export function pendoInitialize({ tenant, user }: MetaDataOptions) {
  if (pendo) {
    getPendoMetaData({
      tenant,
      user,
      useFeatureFlag: true
    }).then(options => {
      if (options) {
        pendo.initialize(options)
      }
    })
  }
}

export function pendoIdentify({ tenant, user }: MetaDataOptions) {
  if (pendo) {
    getPendoMetaData({
      tenant,
      user,
      useFeatureFlag: true
    }).then(options => {
      if (options) {
        pendo.identify(options)
      }
    })
  }
}

export function pendoUpdateOptions({ tenant, user }: MetaDataOptions) {
  if (pendo) {
    getPendoMetaData({
      tenant,
      user,
      useFeatureFlag: true
    }).then(options => {
      if (options) {
        pendo.updateOptions(options)
      }
    })
  }
}
